import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpRight, Truck } from 'lucide-react';
import { Badge } from '@proerp/ui';
import { formatTime } from '@/lib/format';
import { useTransports } from '@/features/transport/transportApi';
import {
  countByDirection,
  endOfLocalDay,
  isOverdue,
  sortSchedule,
} from '@/features/transport/transportSchedule';
import { TaskCard, TaskRow } from './TaskCard';

/**
 * Anstehende Transporte (VB-26004) — die Frage „kommt heute was, und geht heute
 * was?" beantwortet, ohne dass jemand ins Modul wechseln muss.
 *
 * Bewusst **heute UND überfällig**, nicht nur heute: ein Transport, der gestern
 * hätte abgeholt werden sollen und noch offen ist, ist dringender als alles von
 * heute — genau der Fall, den man auf dem Dashboard sehen will. Deshalb eine
 * Abfrage mit oberer Grenze „Ende heute" plus `open=true`; die untere Grenze fehlt
 * absichtlich.
 */
export function TransportsTodayWidget() {
  const navigate = useNavigate();
  // Einmal je Render-Zyklus bestimmt: sonst wandert die Tagesgrenze mitten in der
  // Sortierung, wenn der Nutzer die Seite um Mitternacht offen hat.
  const now = useMemo(() => new Date(), []);
  const query = useTransports({ openOnly: true, plannedUntil: endOfLocalDay(now) });

  const schedule = useMemo(() => sortSchedule(query.data?.data ?? [], now), [query.data, now]);
  const counts = countByDirection(schedule);

  return (
    <TaskCard
      title="Transporte heute"
      icon={Truck}
      isLoading={query.isLoading}
      isEmpty={schedule.length === 0}
      emptyText="Heute ist kein Transport eingeplant."
      header={
        schedule.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {counts.inbound} Anlieferung{counts.inbound === 1 ? '' : 'en'} · {counts.outbound} Abholung
            {counts.outbound === 1 ? '' : 'en'}
          </span>
        ) : undefined
      }
    >
      {schedule.slice(0, 6).map((transport) => {
        const inbound = transport.direction === 'INBOUND';
        const overdue = isOverdue(transport.plannedAt, now);
        const reference = inbound
          ? transport.purchaseOrders.map((order) => order.number).join(', ')
          : (transport.projectCode ?? '');
        return (
          <TaskRow
            key={transport.id}
            onClick={() => navigate(`/transporte/${transport.id}`)}
            left={
              <span className="flex min-w-0 items-center gap-1.5">
                {inbound ? (
                  <ArrowDownLeft className="h-4 w-4 shrink-0 text-info-500" strokeWidth={1.6} />
                ) : (
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.6} />
                )}
                <span className="font-mono text-xs text-foreground">{transport.number}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {reference || transport.locationText?.split('\n')[0] || (inbound ? 'Anlieferung' : 'Abholung')}
                </span>
              </span>
            }
            right={
              overdue ? (
                <Badge tone="warning">überfällig</Badge>
              ) : (
                <Badge tone="neutral">{formatTime(transport.plannedAt as string)}</Badge>
              )
            }
          />
        );
      })}
    </TaskCard>
  );
}
